/**
 * Página de Inicio - Home
 *
 * Diseño único con hero banner que integra toda la info
 * Estilo basado en la web oficial de MET Energía España
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Grid,
  Button,
  Box,
} from '@mui/material';
import {
  ArrowForward as ArrowForwardIcon,
  CalendarMonth,
  Assessment,
  Gavel,
  AccountBalance,
  ListAlt,
  Article,
  FormatSize,
  Summarize,
} from '@mui/icons-material';
import { COLORS } from '../theme/theme';

const Home = () => {
  const navigate = useNavigate();

  const features = [
    { icon: <CalendarMonth />, title: 'Plazos y fechas clave', desc: 'Identifica fechas límite y plazos de presentación' },
    { icon: <Assessment />, title: 'Criterios de valoración', desc: 'Extrae criterios automáticos y discrecionales' },
    { icon: <Gavel />, title: 'Requisitos de solvencia', desc: 'Requisitos técnicos, económicos y profesionales' },
    { icon: <AccountBalance />, title: 'Valor del contrato', desc: 'Presupuesto, lotes y condiciones económicas' },
  ];

  const ofertaFeatures = [
    { icon: <ListAlt />, title: 'Detección de índice', desc: 'Detecta automáticamente los criterios de adjudicación del pliego' },
    { icon: <Article />, title: 'Estructura de oferta', desc: 'Genera las secciones que debe contener la memoria técnica' },
    { icon: <FormatSize />, title: 'Requisitos de formato', desc: 'Extrae fuente, márgenes, páginas máximas, etc.' },
    { icon: <Summarize />, title: 'Análisis por secciones', desc: 'Resume qué hay que escribir en cada apartado' },
  ];

  return (
    <Container maxWidth="lg">
      {/* Banners en paralelo */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Banner Analizador de Licitaciones */}
        <Grid item xs={12} md={6}>
          <Box
            sx={{
              borderRadius: 2,
              overflow: 'hidden',
              position: 'relative',
              backgroundImage: 'url(https://images.unsplash.com/photo-1509391366360-2e959784a276?w=1200&q=80)',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              height: '100%',
              minHeight: 340,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                top: 0, left: 0, right: 0, bottom: 0,
                background: 'linear-gradient(135deg, rgba(0,98,114,0.92) 0%, rgba(26,45,61,0.88) 100%)',
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                top: 0, left: 0, right: 0, bottom: 0,
                opacity: 0.04,
                backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.8) 1px, transparent 1px)',
                backgroundSize: '24px 24px',
              }}
            />

            <Box sx={{ position: 'relative', zIndex: 1, p: { xs: 3, md: 4 }, flex: 1, display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <img
                  src="/logo-met.png"
                  alt="MET Energía"
                  style={{ height: 36, filter: 'brightness(0) invert(1)', marginRight: 14 }}
                />
                <Box>
                  <Typography
                    sx={{
                      color: '#ffffff',
                      fontWeight: 800,
                      fontSize: { xs: '1.1rem', md: '1.35rem' },
                      letterSpacing: '0.08em',
                      textTransform: 'uppercase',
                      lineHeight: 1.1,
                    }}
                  >
                    Analizador de Licitaciones
                  </Typography>
                  <Typography
                    sx={{
                      color: 'rgba(255,255,255,0.5)',
                      fontSize: '0.7rem',
                      letterSpacing: '0.15em',
                      textTransform: 'uppercase',
                      mt: 0.3,
                    }}
                  >
                    MET Energía España
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ width: 40, height: 3, backgroundColor: '#ffffff', mb: 2 }} />

              <Typography
                sx={{
                  color: 'rgba(255,255,255,0.75)',
                  fontSize: '0.9rem',
                  lineHeight: 1.6,
                  mb: 2,
                  flex: 1,
                }}
              >
                Extrae y estructura automáticamente la información clave de tus
                documentos de licitación de energía con inteligencia artificial.
              </Typography>

              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                  <img src="/OpenAI_Logo.svg.png" alt="OpenAI" style={{ height: 16, filter: 'brightness(0) invert(1)', opacity: 0.6 }} />
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                    OpenAI + RAG
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                  <img src="/Microsoft_Excel-Logo.wine.png" alt="Excel" style={{ height: 18, filter: 'brightness(0) invert(1)', opacity: 0.6 }} />
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                    Excel exportable
                  </Typography>
                </Box>
              </Box>

              <Button
                variant="contained"
                endIcon={<ArrowForwardIcon />}
                onClick={() => navigate('/analizar')}
                fullWidth
                sx={{
                  background: 'rgba(255,255,255,0.15)',
                  border: '1px solid rgba(255,255,255,0.3)',
                  fontWeight: 700,
                  fontSize: '0.8rem',
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  py: 1.2,
                  '&:hover': {
                    background: 'rgba(255,255,255,0.25)',
                  },
                }}
              >
                Ir al Analizador
              </Button>
            </Box>
          </Box>
        </Grid>

        {/* Banner Generador de Ofertas */}
        <Grid item xs={12} md={6}>
          <Box
            sx={{
              borderRadius: 2,
              overflow: 'hidden',
              position: 'relative',
              backgroundImage: 'url(https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=1200&q=80)',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              height: '100%',
              minHeight: 340,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                top: 0, left: 0, right: 0, bottom: 0,
                background: 'linear-gradient(135deg, rgba(26,45,61,0.92) 0%, rgba(0,98,114,0.88) 100%)',
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                top: 0, left: 0, right: 0, bottom: 0,
                opacity: 0.04,
                backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.8) 1px, transparent 1px)',
                backgroundSize: '24px 24px',
              }}
            />

            <Box sx={{ position: 'relative', zIndex: 1, p: { xs: 3, md: 4 }, flex: 1, display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <img
                  src="/logo-met.png"
                  alt="MET Energía"
                  style={{ height: 36, filter: 'brightness(0) invert(1)', marginRight: 14 }}
                />
                <Box>
                  <Typography
                    sx={{
                      color: '#ffffff',
                      fontWeight: 800,
                      fontSize: { xs: '1.1rem', md: '1.35rem' },
                      letterSpacing: '0.08em',
                      textTransform: 'uppercase',
                      lineHeight: 1.1,
                    }}
                  >
                    Generador de Ofertas
                  </Typography>
                  <Typography
                    sx={{
                      color: 'rgba(255,255,255,0.5)',
                      fontSize: '0.7rem',
                      letterSpacing: '0.15em',
                      textTransform: 'uppercase',
                      mt: 0.3,
                    }}
                  >
                    MET Energía España
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ width: 40, height: 3, backgroundColor: '#ffffff', mb: 2 }} />

              <Typography
                sx={{
                  color: 'rgba(255,255,255,0.75)',
                  fontSize: '0.9rem',
                  lineHeight: 1.6,
                  mb: 2,
                  flex: 1,
                }}
              >
                Analiza los pliegos de licitación, detecta los criterios de adjudicación
                y genera automáticamente la estructura de la memoria técnica de tu oferta.
              </Typography>

              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                  <img src="/OpenAI_Logo.svg.png" alt="OpenAI" style={{ height: 16, filter: 'brightness(0) invert(1)', opacity: 0.6 }} />
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                    OpenAI + LangGraph
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.95rem' }}>
                    &#x1F4DD;
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                    Word exportable
                  </Typography>
                </Box>
              </Box>

              <Button
                variant="contained"
                endIcon={<ArrowForwardIcon />}
                onClick={() => navigate('/generar-propuesta')}
                fullWidth
                sx={{
                  background: 'rgba(255,255,255,0.15)',
                  border: '1px solid rgba(255,255,255,0.3)',
                  fontWeight: 700,
                  fontSize: '0.8rem',
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  py: 1.2,
                  '&:hover': {
                    background: 'rgba(255,255,255,0.25)',
                  },
                }}
              >
                Ir al Generador de Ofertas
              </Button>
            </Box>
          </Box>
        </Grid>
      </Grid>

      {/* Grid de funcionalidades - ambas en una fila de 8 */}
      <Grid container spacing={2} sx={{ mb: 5 }}>
        {features.map((f, i) => (
          <Grid item xs={6} sm={3} md={1.5} key={`a-${i}`}>
            <Box
              sx={{
                backgroundColor: '#ffffff',
                borderRadius: 2,
                p: 2,
                textAlign: 'center',
                border: '1px solid #e8ecf0',
                boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                height: '100%',
                transition: 'border-color 0.2s',
                '&:hover': {
                  borderColor: COLORS.teal,
                },
              }}
            >
              <Box sx={{ color: COLORS.teal, mb: 1, '& .MuiSvgIcon-root': { fontSize: 28 } }}>
                {f.icon}
              </Box>
              <Typography sx={{ fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.3 }}>
                {f.title}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.68rem' }}>
                {f.desc}
              </Typography>
            </Box>
          </Grid>
        ))}
        {ofertaFeatures.map((f, i) => (
          <Grid item xs={6} sm={3} md={1.5} key={`o-${i}`}>
            <Box
              sx={{
                backgroundColor: '#ffffff',
                borderRadius: 2,
                p: 2,
                textAlign: 'center',
                border: '1px solid #e8ecf0',
                boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                height: '100%',
                transition: 'border-color 0.2s',
                '&:hover': {
                  borderColor: COLORS.navy,
                },
              }}
            >
              <Box sx={{ color: COLORS.navy, mb: 1, '& .MuiSvgIcon-root': { fontSize: 28 } }}>
                {f.icon}
              </Box>
              <Typography sx={{ fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.3 }}>
                {f.title}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.68rem' }}>
                {f.desc}
              </Typography>
            </Box>
          </Grid>
        ))}
      </Grid>

      {/* Sección "Cómo empezar" */}
      <Box
        sx={{
          p: 4,
          backgroundColor: '#ffffff',
          borderRadius: 2,
          border: '1px solid #e2e8f0',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04)',
        }}
      >
        <Typography variant="h4" gutterBottom sx={{ color: COLORS.teal }}>
          ¿Cómo empezar?
        </Typography>

        <Grid container spacing={3} sx={{ mt: 1 }}>
          {[
            { n: '1', title: 'Sube tus PDFs', desc: 'Sube los documentos PDF de la licitación que quieras analizar' },
            { n: '2', title: 'Analiza', desc: 'La IA extrae automáticamente plazos, requisitos y criterios' },
            { n: '3', title: 'Descarga', desc: 'Revisa los resultados y descárgalos en Excel listo para usar' },
          ].map((step) => (
            <Grid item xs={12} md={4} key={step.n}>
              <Box sx={{ textAlign: 'center' }}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: '10px',
                    background: COLORS.teal,
                    color: '#ffffff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 2,
                    fontSize: '1.3rem',
                    fontWeight: 800,
                  }}
                >
                  {step.n}
                </Box>
                <Typography variant="h6" gutterBottom>
                  {step.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {step.desc}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Container>
  );
};

export default Home;
